/**
 * `src/debug` public surface. Owner: Platform (QA). F1: `window.__marg` (`api.ts`, architecture §8.1), URL params
 * (`params.ts`, §8.3), the error log, the perf overlay, fixtures (`fixtures/<id>.json`, GDD §4.15) and playlogs. As a
 * feature it registers the Art Lab screen and mounts the `render` and `ui` namespaces (GDD §4.15: debug → render + ui;
 * art §12 and ui-ux §22.2 own their final member lists — F2/P10 and F3 extend them).
 */
import type { AppContext, FeatureModule, Renderer, ScreenDef } from '../shared';
import { EngineRenderer, createRenderDebug, type RenderDebugNamespace } from '../engine';
import { heightClass, layoutMode, stubScreen } from '../ui';

export { installDebugApi, NAMESPACES, type DebugHost, type MargApi } from './api';
export { parseBootParams, parseDuration, parseWall, isFixtureId, type BootParams, type MotionLevel, type OverlayId, type SimulateKind } from './params';
export { ErrorLog } from './errors';
export { PerfOverlay, type PerfSample } from './overlays';
export { loadFixtureFile, fixtureIds, type FixtureFile } from './fixtures';

const debugScreens: ScreenDef[] = [
  stubScreen({ id: 'debug/artlab', aliases: ['artlab'], titleKey: 'debug.screen.artlab', needs3d: true, kind: 'debug' }),
];

/** Art §12 `__marg.render`: F2's `createRenderDebug` for the live engine renderer (re-bound after a re-create), with
 *  contract-level fallbacks for any other renderer and `null` answers in lite mode. */
function renderNamespace(get: () => Renderer | null): Record<string, unknown> {
  const cache = new WeakMap<Renderer, RenderDebugNamespace>();
  const eng = (): RenderDebugNamespace | null => {
    const r = get();
    if (!(r instanceof EngineRenderer)) return null;
    let ns = cache.get(r);
    if (!ns) { ns = createRenderDebug(r); cache.set(r, ns); }
    return ns;
  };
  const keys: Array<keyof RenderDebugNamespace> = ['getQuality', 'setQuality', 'caps', 'setDebugView', 'stats', 'fx', 'freeze', 'unfreeze',
    'samplePixel', 'capture', 'debugBounds', 'debugLift', 'setHpPct', 'palette', 'errors', 'loseDevice'];
  const out: Record<string, unknown> = {};
  for (const k of keys) out[k] = (...a: unknown[]) => { const ns = eng(); return ns ? (ns[k] as (...x: unknown[]) => unknown)(...a) : fallback(k, get(), a); };
  out.setView = out.setDebugView;
  out.fillSlots = (n: number) => get()?.debug.fillSlots(n);
  out.setDebug = (flags: Parameters<Renderer['setDebug']>[0]) => get()?.setDebug(flags);
  return out;
}

function fallback(k: string, r: Renderer | null, a: unknown[]): unknown {
  if (!r) return k === 'errors' ? [] : null;
  switch (k) {
    case 'getQuality': return r.quality;
    case 'caps': return r.caps();
    case 'stats': return r.stats;
    case 'setDebugView': return r.debug.setView((a[0] as string | null) ?? null);
    case 'samplePixel': return r.debug.samplePixel(a[0] as number, a[1] as number);
    case 'capture': return r.debug.capture();
    case 'debugBounds': return r.debug.bounds();
    case 'debugLift': return r.debug.lift(a[0] as number, a[1] as number);
    default: return null;
  }
}

interface TapEntry { t: number; x: number; y: number; target: string | null; route: string }
const taps: TapEntry[] = [];

function describe(t: EventTarget | null): string | null {
  if (!(t instanceof Element)) return null;
  const el = t.closest('[data-tab], button, a, [role="button"], [data-frequent]') ?? t;
  const id = el.id ? `#${el.id}` : '';
  const cls = typeof el.className === 'string' && el.className ? `.${el.className.trim().split(/\s+/)[0]}` : '';
  const tab = (el as HTMLElement).dataset?.tab ? `[data-tab=${(el as HTMLElement).dataset.tab}]` : '';
  return `${el.tagName.toLowerCase()}${id}${cls}${tab}`;
}

export const debug: FeatureModule = {
  key: 'debug',
  screens: debugScreens,
  onBoot(ctx: AppContext) {
    ctx.input.onPointer('tap', (e) => {
      taps.push({ t: ctx.clock.wallNow(), x: e.x, y: e.y, target: describe(e.target), route: location.hash });
      if (taps.length > 1000) taps.shift();
    });
  },
  debugApi: (ctx: AppContext) => ({
    render: renderNamespace(() => ctx.renderer),
    ui: {
      state: () => {
        const root = document.documentElement;
        const ui = (ctx.store.state.slices.ui ?? {}) as { ftueStep?: unknown };
        return {
          route: location.hash, tab: document.querySelector('.mc-tab[aria-current="page"]')?.getAttribute('data-tab') ?? null,
          overlays: [...document.querySelectorAll('.mc-layer-sheet > .mc-screen')].map((e) => (e as HTMLElement).dataset.screenId ?? ''),
          layoutMode: layoutMode(innerWidth, innerHeight), heightClass: heightClass(innerHeight),
          display: root.dataset.display ?? (window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser'),
          motion: root.dataset.motion ?? 'full', theme: 'day', lite: root.dataset.lite === '1',
          textScale: Number(root.dataset.textscale ?? 100) / 100, leftHanded: root.dataset.lefthand === '1',
          ftueStep: typeof ui.ftueStep === 'string' ? ui.ftueStep : null,
        };
      },
      goto: (route: string) => ctx.router.goto(route),
      layout: () => ({}),
      debugBounds: () => ({}),
      primary: () => null,
      taps: () => taps.slice(),
      coach: { log: () => [] as unknown[] },
    },
  }),
};
